import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, KeyRound, Shield, RefreshCw, Store, Users, Percent } from "lucide-react";

const MODULE_LABELS = {
  dashboard: "Dashboard", shops: "Shops", verifications: "Verifications",
  trial_followups: "Trial Follow-ups", customers: "Customers", loans: "Loans",
  payments: "Payments", recovery: "Recovery", reports: "Reports",
  subscriptions: "Subscriptions", refer_earn: "Refer & Earn", staff: "Staff",
  sub_admins: "Sub Admins", withdrawals: "Withdrawals", commissions: "Commissions",
  support: "Support", analytics: "Analytics", settings: "Settings",
};

const fmtPKR = (n) => `PKR ${Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;

export default function AdminSubAdmins() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setRows((await api.get("/admin/sub-admins")).data); }
    catch (err) { toast.error(err?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id, email) => {
    if (!window.confirm(`Delete sub-admin ${email}?`)) return;
    try {
      await api.delete(`/admin/sub-admins/${id}`);
      toast.success("Deleted"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-sub-admins-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-white flex items-center gap-2"><Shield className="w-7 h-7 text-emerald-400" /> Sub Admins</h1>
          <p className="text-slate-400 text-sm mt-1">Platform-level operators with configurable sidebar permissions and per-user commission rate (default 40 %).</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="subadmin-refresh-btn"><RefreshCw className="w-4 h-4 mr-1.5" /> Refresh</Button>
          <Button onClick={() => setShowCreate(true)} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="subadmin-create-btn"><Plus className="w-4 h-4 mr-1.5" /> New Sub Admin</Button>
        </div>
      </div>

      {loading ? <div className="grid place-items-center py-16"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div> :
       rows.length === 0 ? <div className="rounded-md border border-slate-800 bg-[#0F131C] p-8 text-center text-slate-400 text-sm">No sub-admins yet. Click <b>New Sub Admin</b> to create one.</div> :
        <div className="rounded-md border border-slate-800 bg-[#0F131C] overflow-hidden" data-testid="subadmin-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-[10px] uppercase tracking-widest text-slate-400">
              <tr>
                <th className="text-left px-4 py-2.5">Sub Admin</th>
                <th className="text-right px-4 py-2.5">Shops</th>
                <th className="text-right px-4 py-2.5">Rate</th>
                <th className="text-right px-4 py-2.5">Pending</th>
                <th className="text-right px-4 py-2.5">Paid</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-900/50" data-testid={`subadmin-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="text-white font-semibold">{r.full_name}</div>
                    <div className="text-xs text-slate-500">{r.email}{r.is_active === false ? " · Inactive" : ""}</div>
                  </td>
                  <td className="text-right px-4 py-3 text-slate-200">{r.assigned_shop_count}</td>
                  <td className="text-right px-4 py-3 text-emerald-400 font-mono">{r.sub_admin_commission_rate}%</td>
                  <td className="text-right px-4 py-3 text-amber-300">{fmtPKR(r.commission_pending)}</td>
                  <td className="text-right px-4 py-3 text-emerald-300">{fmtPKR(r.commission_paid)}</td>
                  <td className="text-right px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      <button onClick={() => setEditing(r)} className="text-xs px-2 py-1 rounded border border-slate-700 text-slate-200 hover:bg-slate-800" data-testid={`subadmin-edit-${r.id}`}>Edit</button>
                      <button onClick={() => del(r.id, r.email)} className="text-xs px-2 py-1 rounded border border-rose-700 text-rose-300 hover:bg-rose-950/40" data-testid={`subadmin-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onDone={load} />}
      {editing && <EditModal row={editing} onClose={() => setEditing(null)} onDone={load} />}
    </div>
  );
}

function CreateModal({ onClose, onDone }) {
  const [form, setForm] = useState({ email: "", full_name: "", password: "", commission_rate: 40, mobile: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/admin/sub-admins", form); toast.success("Sub Admin created"); onDone(); onClose(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={onClose} data-testid="subadmin-create-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#0F131C] border border-slate-800 rounded-md p-5 w-full max-w-md space-y-3">
        <h2 className="font-display text-xl font-bold text-white">New Sub Admin</h2>
        <div><Label className="text-slate-300">Full Name</Label><Input required minLength={2} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="bg-slate-900 border-slate-700 text-white" data-testid="subadmin-create-name" /></div>
        <div><Label className="text-slate-300">Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-900 border-slate-700 text-white" data-testid="subadmin-create-email" /></div>
        <div><Label className="text-slate-300">Mobile (optional)</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="bg-slate-900 border-slate-700 text-white" data-testid="subadmin-create-mobile" /></div>
        <div><Label className="text-slate-300">Password (min 6)</Label><Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-slate-900 border-slate-700 text-white" data-testid="subadmin-create-password" /></div>
        <div><Label className="text-slate-300">Commission Rate %</Label><Input type="number" min={0} max={100} step={0.5} value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: Number(e.target.value) })} className="bg-slate-900 border-slate-700 text-white" data-testid="subadmin-create-rate" /></div>
        <div className="flex gap-2 justify-end pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="border-slate-700 text-slate-200" data-testid="subadmin-create-cancel">Cancel</Button>
          <Button type="submit" disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="subadmin-create-submit">{busy ? "Creating..." : "Create"}</Button>
        </div>
      </form>
    </div>
  );
}

function EditModal({ row, onClose, onDone }) {
  const [form, setForm] = useState({
    full_name: row.full_name,
    commission_rate: row.sub_admin_commission_rate,
    is_active: row.is_active !== false,
    permissions: row.sub_admin_permissions || {},
  });
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const togglePerm = (k) => setForm((f) => ({ ...f, permissions: { ...f.permissions, [k]: !f.permissions[k] } }));

  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/sub-admins/${row.id}`, form); toast.success("Saved"); onDone(); onClose(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const resetPw = async () => {
    if (pw.length < 6) return toast.error("Password kam az kam 6 characters");
    setBusy(true);
    try { await api.post(`/admin/sub-admins/${row.id}/reset-password`, { password: pw }); toast.success("Password reset"); setPw(""); }
    catch (err) { toast.error(err?.response?.data?.detail || "Reset failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4 overflow-y-auto" onClick={onClose} data-testid="subadmin-edit-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0F131C] border border-slate-800 rounded-md p-5 w-full max-w-3xl space-y-4 my-8">
        <h2 className="font-display text-xl font-bold text-white">Edit — {row.email}</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-slate-300">Full Name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="bg-slate-900 border-slate-700 text-white" data-testid="subadmin-edit-name" /></div>
          <div><Label className="text-slate-300 flex items-center gap-1.5"><Percent className="w-3.5 h-3.5" /> Commission Rate</Label><Input type="number" min={0} max={100} step={0.5} value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: Number(e.target.value) })} className="bg-slate-900 border-slate-700 text-white" data-testid="subadmin-edit-rate" /></div>
        </div>

        <div className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-900 px-3 py-2">
          <div className="text-sm text-slate-200">Active</div>
          <Switch checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: !!v })} data-testid="subadmin-edit-active" />
        </div>

        <div className="rounded-md border border-slate-800 bg-slate-900/50 p-3" data-testid="subadmin-edit-perms">
          <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-400 mb-2">Sidebar Permissions</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {Object.entries(MODULE_LABELS).map(([k, label]) => (
              <div key={k} className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-900 px-2 py-1.5">
                <div className="text-xs text-slate-200">{label}</div>
                <Switch checked={!!form.permissions[k]} onCheckedChange={() => togglePerm(k)} data-testid={`subadmin-perm-${k}`} />
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-md border border-amber-900/50 bg-amber-950/20 p-3 space-y-2" data-testid="subadmin-edit-reset-pw">
          <div className="text-[11px] uppercase tracking-widest font-bold text-amber-400 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Reset Password</div>
          <div className="flex gap-2">
            <Input type="password" placeholder="new password (min 6)" value={pw} onChange={(e) => setPw(e.target.value)} className="bg-slate-900 border-slate-700 text-white" data-testid="subadmin-edit-newpw" />
            <Button type="button" onClick={resetPw} disabled={busy || pw.length < 6} variant="outline" className="border-amber-700 text-amber-300 hover:bg-amber-950/40" data-testid="subadmin-edit-resetpw-btn">Reset</Button>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="border-slate-700 text-slate-200" data-testid="subadmin-edit-cancel">Close</Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="subadmin-edit-save">{busy ? "Saving..." : "Save"}</Button>
        </div>
      </div>
    </div>
  );
}
